/**
 * useRoadmaps Hook
 *
 * React Query hooks for roadmap operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  roadmapsService,
  roadmapThemesService,
  roadmapItemsService,
  roadmapItemCommentsService,
  CreateRoadmapDto,
  UpdateRoadmapDto,
  CreateRoadmapThemeDto,
  UpdateRoadmapThemeDto,
  CreateRoadmapItemDto,
  UpdateRoadmapItemDto,
  CreateRoadmapItemCommentDto,
  UpdateRoadmapItemCommentDto,
} from '../services/roadmaps.service';
import { unwrapResponse } from '../types';

import type {
  Roadmap,
  RoadmapWithCompany,
  RoadmapFull,
  RoadmapTheme,
  RoadmapThemeWithRoadmap,
  RoadmapItemWithDetails,
  RoadmapItemComment,
  RoadmapItemCommentWithReplies,
  RoadmapItemHistory,
} from '@medsuite/models';

// Query keys
export const roadmapKeys = {
  all: ['roadmaps'] as const,
  lists: () => [...roadmapKeys.all, 'list'] as const,
  list: () => [...roadmapKeys.lists()] as const,
  details: () => [...roadmapKeys.all, 'detail'] as const,
  detail: (id: number) => [...roadmapKeys.details(), id] as const,
  full: (id: number) => [...roadmapKeys.all, 'full', id] as const,
};

export const roadmapThemeKeys = {
  all: ['roadmap-themes'] as const,
  lists: () => [...roadmapThemeKeys.all, 'list'] as const,
  list: (roadmapId?: number) => [...roadmapThemeKeys.lists(), roadmapId] as const,
  details: () => [...roadmapThemeKeys.all, 'detail'] as const,
  detail: (id: number) => [...roadmapThemeKeys.details(), id] as const,
};

export const roadmapItemKeys = {
  all: ['roadmap-items'] as const,
  lists: () => [...roadmapItemKeys.all, 'list'] as const,
  list: (params?: { roadmap_id?: number; theme_id?: number; investment_id?: number }) =>
    [...roadmapItemKeys.lists(), params] as const,
  details: () => [...roadmapItemKeys.all, 'detail'] as const,
  detail: (id: number) => [...roadmapItemKeys.details(), id] as const,
  history: (id: number) => [...roadmapItemKeys.all, 'history', id] as const,
};

export const roadmapItemCommentKeys = {
  all: ['roadmap-item-comments'] as const,
  lists: () => [...roadmapItemCommentKeys.all, 'list'] as const,
  list: (itemId: number) => [...roadmapItemCommentKeys.lists(), itemId] as const,
};

// ========== ROADMAPS HOOKS ==========

/**
 * Fetch all roadmaps
 */
export function useRoadmaps(
  options?: Omit<UseQueryOptions<RoadmapWithCompany[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<RoadmapWithCompany[], Error>({
    queryKey: roadmapKeys.list(),
    queryFn: async () => {
      const response = await roadmapsService.getAll();
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Fetch single roadmap
 */
export function useRoadmap(
  id: number,
  options?: Omit<UseQueryOptions<RoadmapWithCompany, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<RoadmapWithCompany, Error>({
    queryKey: roadmapKeys.detail(id),
    queryFn: async () => {
      const response = await roadmapsService.getById(id);
      return unwrapResponse(response.data);
    },
    enabled: id > 0,
    ...options,
  });
}

/**
 * Fetch roadmap with themes and items (for Gantt view)
 */
export function useRoadmapFull(
  id: number,
  options?: Omit<UseQueryOptions<RoadmapFull, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<RoadmapFull, Error>({
    queryKey: roadmapKeys.full(id),
    queryFn: async () => {
      const response = await roadmapsService.getFull(id);
      return unwrapResponse(response.data);
    },
    enabled: id > 0,
    ...options,
  });
}

/**
 * Create roadmap
 */
export function useCreateRoadmap(options?: UseMutationOptions<Roadmap, Error, CreateRoadmapDto>) {
  const queryClient = useQueryClient();

  return useMutation<Roadmap, Error, CreateRoadmapDto>({
    mutationFn: async (data) => {
      const response = await roadmapsService.create(data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roadmapKeys.lists() });
    },
    ...options,
  });
}

/**
 * Update roadmap
 */
export function useUpdateRoadmap(
  options?: UseMutationOptions<Roadmap, Error, { id: number; data: UpdateRoadmapDto }>
) {
  const queryClient = useQueryClient();

  return useMutation<Roadmap, Error, { id: number; data: UpdateRoadmapDto }>({
    mutationFn: async ({ id, data }) => {
      const response = await roadmapsService.update(id, data);
      return unwrapResponse(response.data);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: roadmapKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.full(id) });
    },
    ...options,
  });
}

/**
 * Delete roadmap
 */
export function useDeleteRoadmap(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await roadmapsService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roadmapKeys.lists() });
    },
    ...options,
  });
}

// ========== ROADMAP THEMES HOOKS ==========

/**
 * Fetch roadmap themes
 */
export function useRoadmapThemes(
  roadmapId?: number,
  options?: Omit<
    UseQueryOptions<RoadmapTheme[] | RoadmapThemeWithRoadmap[], Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<RoadmapTheme[] | RoadmapThemeWithRoadmap[], Error>({
    queryKey: roadmapThemeKeys.list(roadmapId),
    queryFn: async () => {
      const response = await roadmapThemesService.getAll(roadmapId);
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Create roadmap theme
 */
export function useCreateRoadmapTheme(
  options?: UseMutationOptions<RoadmapTheme, Error, CreateRoadmapThemeDto>
) {
  const queryClient = useQueryClient();

  return useMutation<RoadmapTheme, Error, CreateRoadmapThemeDto>({
    mutationFn: async (data) => {
      const response = await roadmapThemesService.create(data);
      return unwrapResponse(response.data);
    },
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: roadmapThemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.full(data.roadmap_id) });
    },
    ...options,
  });
}

/**
 * Update roadmap theme
 */
export function useUpdateRoadmapTheme(
  options?: UseMutationOptions<RoadmapTheme, Error, { id: number; data: UpdateRoadmapThemeDto }>
) {
  const queryClient = useQueryClient();

  return useMutation<RoadmapTheme, Error, { id: number; data: UpdateRoadmapThemeDto }>({
    mutationFn: async ({ id, data }) => {
      const response = await roadmapThemesService.update(id, data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roadmapThemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.all });
    },
    ...options,
  });
}

/**
 * Delete roadmap theme
 */
export function useDeleteRoadmapTheme(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await roadmapThemesService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roadmapThemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.all });
    },
    ...options,
  });
}

/**
 * Reorder roadmap theme
 */
export function useReorderRoadmapTheme(
  options?: UseMutationOptions<RoadmapTheme[], Error, { id: number; direction: 'up' | 'down' }>
) {
  const queryClient = useQueryClient();

  return useMutation<RoadmapTheme[], Error, { id: number; direction: 'up' | 'down' }>({
    mutationFn: async ({ id, direction }) => {
      const response = await roadmapThemesService.reorder(id, direction);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roadmapThemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.all });
    },
    ...options,
  });
}

// ========== ROADMAP ITEMS HOOKS ==========

/**
 * Fetch roadmap items
 */
export function useRoadmapItems(
  params?: { roadmap_id?: number; theme_id?: number; investment_id?: number },
  options?: Omit<UseQueryOptions<RoadmapItemWithDetails[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<RoadmapItemWithDetails[], Error>({
    queryKey: roadmapItemKeys.list(params),
    queryFn: async () => {
      const response = await roadmapItemsService.getAll(params);
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Fetch single roadmap item
 */
export function useRoadmapItem(
  id: number,
  options?: Omit<UseQueryOptions<RoadmapItemWithDetails, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<RoadmapItemWithDetails, Error>({
    queryKey: roadmapItemKeys.detail(id),
    queryFn: async () => {
      const response = await roadmapItemsService.getById(id);
      return unwrapResponse(response.data);
    },
    enabled: id > 0,
    ...options,
  });
}

/**
 * Create roadmap item
 */
export function useCreateRoadmapItem(
  options?: UseMutationOptions<RoadmapItemWithDetails, Error, CreateRoadmapItemDto>
) {
  const queryClient = useQueryClient();

  return useMutation<RoadmapItemWithDetails, Error, CreateRoadmapItemDto>({
    mutationFn: async (data) => {
      const response = await roadmapItemsService.create(data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roadmapItemKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.all });
    },
    ...options,
  });
}

/**
 * Update roadmap item
 */
export function useUpdateRoadmapItem(
  options?: UseMutationOptions<
    RoadmapItemWithDetails,
    Error,
    { id: number; data: UpdateRoadmapItemDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<RoadmapItemWithDetails, Error, { id: number; data: UpdateRoadmapItemDto }>({
    mutationFn: async ({ id, data }) => {
      const response = await roadmapItemsService.update(id, data);
      return unwrapResponse(response.data);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: roadmapItemKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roadmapItemKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.all });
    },
    ...options,
  });
}

/**
 * Delete roadmap item
 */
export function useDeleteRoadmapItem(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await roadmapItemsService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roadmapItemKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.all });
    },
    ...options,
  });
}

/**
 * Fetch roadmap item change history
 */
export function useRoadmapItemHistory(
  id: number,
  since?: string,
  options?: Omit<UseQueryOptions<RoadmapItemHistory[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<RoadmapItemHistory[], Error>({
    queryKey: roadmapItemKeys.history(id),
    queryFn: async () => {
      const response = await roadmapItemsService.getHistory(id, since);
      return unwrapResponse(response.data);
    },
    enabled: id > 0,
    ...options,
  });
}

/**
 * Set/reset baseline for a roadmap item
 */
export function useSetRoadmapItemBaseline(
  options?: UseMutationOptions<RoadmapItemWithDetails, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<RoadmapItemWithDetails, Error, number>({
    mutationFn: async (id) => {
      const response = await roadmapItemsService.setBaseline(id);
      return unwrapResponse(response.data);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: roadmapItemKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roadmapItemKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: roadmapItemKeys.history(id) });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.all });
    },
    ...options,
  });
}

// ========== ROADMAP ITEM COMMENTS HOOKS ==========

/**
 * Fetch roadmap item comments
 */
export function useRoadmapItemComments(
  itemId: number,
  threaded = false,
  options?: Omit<
    UseQueryOptions<RoadmapItemComment[] | RoadmapItemCommentWithReplies[], Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<RoadmapItemComment[] | RoadmapItemCommentWithReplies[], Error>({
    queryKey: roadmapItemCommentKeys.list(itemId),
    queryFn: async () => {
      const response = await roadmapItemCommentsService.getAll(itemId, threaded);
      return unwrapResponse(response.data);
    },
    enabled: itemId > 0,
    ...options,
  });
}

/**
 * Create roadmap item comment
 */
export function useCreateRoadmapItemComment(
  options?: UseMutationOptions<RoadmapItemComment, Error, CreateRoadmapItemCommentDto>
) {
  const queryClient = useQueryClient();

  return useMutation<RoadmapItemComment, Error, CreateRoadmapItemCommentDto>({
    mutationFn: async (data) => {
      const response = await roadmapItemCommentsService.create(data);
      return unwrapResponse(response.data);
    },
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({
        queryKey: roadmapItemCommentKeys.list(data.roadmap_item_id),
      });
    },
    ...options,
  });
}

/**
 * Update roadmap item comment
 */
export function useUpdateRoadmapItemComment(
  itemId: number,
  options?: UseMutationOptions<
    RoadmapItemComment,
    Error,
    { id: number; data: UpdateRoadmapItemCommentDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<RoadmapItemComment, Error, { id: number; data: UpdateRoadmapItemCommentDto }>({
    mutationFn: async ({ id, data }) => {
      const response = await roadmapItemCommentsService.update(id, data);
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roadmapItemCommentKeys.list(itemId) });
    },
    ...options,
  });
}

/**
 * Delete roadmap item comment
 */
export function useDeleteRoadmapItemComment(
  itemId: number,
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await roadmapItemCommentsService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roadmapItemCommentKeys.list(itemId) });
    },
    ...options,
  });
}
